import Vue from 'vue'
import router from './router'
import store from './stores'

import ElementUI from 'element-ui'
import i18n, { fetchLocaleFromServer } from './i18n'

import App from './App.vue'

import '../public/libs/theme/index.css'
import '../public/libs/icon-font/iconfont.css'

import localeEn from 'element-ui/lib/locale/lang/en'
import localeKo from 'element-ui/lib/locale/lang/ko'

import './assets/css/global.css'
import './assets/css/custom_el.css'
import 'element-ui/lib/theme-chalk/display.css'

import httpPlugin from './plugins/http'
import webSocketPlugin from './plugins/webSocket'
import globalComponents from './plugins/globalComponents'
import utilMixin from './mixins/utilMixin'
import keepNumberCaret from './directives/keepNumberCaret'
import devicePlugin from './plugins/device'
import { BOOT_ACTION, BOOT_RELOAD_ONCE_KEY, bootFailureAction } from './utils/bootRecovery'
import { isEmbedPath } from './utils/routeLayout'
// 정적 import — 부트가 실패한 자리에서 지연 청크를 또 기다릴 수 없다 (12R P9)
import BootFailureView from './views/BootFailureView.vue'

Vue.config.productionTip = false

Vue.use(ElementUI, {
  locale: i18n.locale === 'ko' ? localeKo : localeEn,
})

Vue.use(httpPlugin)
Vue.use(webSocketPlugin)
Vue.use(globalComponents)
Vue.use(devicePlugin)

Vue.mixin(utilMixin)

/** 번호 칸(전화·팩스·사업자번호)이 하이픈을 다시 끼워 그려도 커서를 친 자리에 둔다 — `directives/keepNumberCaret` */
Vue.directive('keep-number-caret', keepNumberCaret)

/**
 * **첫 라우트가 정해진 뒤에 붙인다.**
 *
 * vue-router 의 최초 내비게이션은 비동기다 — 그 전의 `$route` 는 아직 `/`(START)라 `meta` 가 비어 있다.
 * 그대로 그리면 앱이 **주소와 다른 판단**을 한 프레임 한다: 레이아웃이 기본 셸로 잡혀 SideNav 가 붙고
 * `/mainMenu` 를 부르며(임베드 창에서도), 로그인 정보가 없으면 `isLogin` 감시자가 `/login` 으로 밀어낸다.
 * POS 안의 임베드 창에서는 그 한 프레임이 곧 이탈이라 되돌아올 길이 없다.
 *
 * 인스턴스 **생성**과 **마운트**를 가른다: 첫 내비게이션은 라우터가 Vue 인스턴스에 설치될 때 비로소 시작하므로
 * `new Vue(...)` 를 `onReady` 안에 넣으면 서로 기다리는 교착이 된다. 만들어 두고 `onReady` 에서 붙이기만 한다.
 */
const app = new Vue({
  router,
  i18n,
  store,
  render: (h) => h(App),
})

/**
 * ★ **실패한 최초 내비게이션은 정적 복구 화면을 그린다** (2026-09-03 리뷰 지적 · 12R P9).
 *
 * `onReady(cb, errorCb)` 는 최초 내비게이션이 성공했을 때만 `cb` 를 부른다 — 오류 콜백을 주지 않으면 실패한
 * 그 순간부터 `#app` 이 빈 채로 남아 오류 화면도 되돌아갈 길도 없는 백지가 된다. 그렇다고 앱을 그냥 붙여도
 * 라우터는 START 라 `router-view` 가 비고 임베드 경로는 셸조차 없어 **백지가 그대로**였다(8R~12R 재지적). 판정은
 * `utils/bootRecovery` 한 곳이 갖고, 답이 `fallback` 이면 라우터·스토어·지연 청크에 기대지 않는 `BootFailureView`
 * 를 `#app` 에 그린다 — 임베드 경로(`isEmbedPath`)면 「POS 에서 다시 열어 주세요」, 아니면 「새로고침」.
 */
function mountBootFailure() {
  const embed = isEmbedPath(window.location.pathname)
  new Vue({ render: (h) => h(BootFailureView, { props: { embed } }) }).$mount('#app')
}

router.onReady(
  () => {
    try {
      sessionStorage.removeItem(BOOT_RELOAD_ONCE_KEY)
    } catch (e) {
      // sessionStorage 를 못 쓰는 환경이어도 마운트는 막지 않는다
    }
    app.$mount('#app')
    // 부트스트랩 요청은 라우트 문맥이 선 뒤에 (14R P56) — `afterEach` 가 확정 라우트를 커밋한 뒤라 요청 시점 스냅샷이
    // 임베드 창에서는 임베드로 찍히고, 그 403 이 세션을 끝내지 않는다. 마운트를 막지 않는다(로케일은 오면 갈아 끼운다)
    fetchLocaleFromServer(i18n.locale)
  },
  (error) => {
    let alreadyReloaded = true
    try {
      alreadyReloaded = sessionStorage.getItem(BOOT_RELOAD_ONCE_KEY) === '1'
    } catch (e) {
      // 읽지 못하면 새로고침하지 않는다 — 무한 새로고침이 백지보다 나쁘다
    }
    if (bootFailureAction(error, alreadyReloaded) === BOOT_ACTION.RELOAD) {
      try {
        sessionStorage.setItem(BOOT_RELOAD_ONCE_KEY, '1')
      } catch (e) {
        // 표시를 못 남기면 새로고침하지 않는다(무한 새로고침 방지) — 복구 화면이 답이다
        return mountBootFailure()
      }
      window.location.reload()
      return
    }
    mountBootFailure()
  }
)
